import { ShadeScale } from './theme.types';

/**
 * Lightness ladder used to derive a Tailwind-style 50→950 palette from a
 * single brand hex color. Mirrors Tailwind's perceptual ladder closely enough
 * that PrimeNG Aura's `--p-primary-{50..950}` consumers render with no visual
 * surprises across typical brand colors.
 *
 * - `null` for `500` means "use the source color's own lightness", so the
 *   anchor shade equals the input hex exactly. All other slots replace the
 *   source's lightness with a fixed target while preserving its hue + sat.
 */
export const SHADE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const satisfies readonly (keyof ShadeScale)[];

const SHADE_LIGHTNESS: Readonly<Record<keyof ShadeScale, number | null>> = {
  50: 97,
  100: 94,
  200: 86,
  300: 77,
  400: 66,
  500: null,
  600: 45,
  700: 36,
  800: 27,
  900: 18,
  950: 11,
};

const HEX_RE = /^#?([0-9a-f]{6})$/i;

/**
 * Generates a stable 50→950 shade map from a single 6-digit hex color. Pure +
 * synchronous so it's safe to call on every theme change without batching.
 *
 * @throws when `hex` is not parseable; callers should fall back to the
 *   default theme.
 */
export function generateShadeScale(hex: string): ShadeScale {
  const { h, s, l } = hexToHsl(hex);
  const out = {} as Record<keyof ShadeScale, string>;
  for (const key of SHADE_KEYS) {
    const target = SHADE_LIGHTNESS[key];
    out[key] = hslToHex(h, s, target ?? l);
  }
  return out as ShadeScale;
}

/**
 * Decides whether white or near-black yields better contrast against `hex`.
 * Used for `--p-primary-contrast-color`. Returns the chosen ink's hex.
 *
 * Uses WCAG 2.1's sRGB **relative luminance** (not HSL lightness) because
 * HSL lightness is non-perceptual: pure yellow `#facc15` has HSL L ≈ 53%
 * but reads as bright as white at a glance and needs dark ink to remain
 * legible.
 */
export function pickContrastInk(hex: string): string {
  return relativeLuminance(hex) < 0.5 ? '#ffffff' : '#0f172a';
}

function relativeLuminance(hex: string): number {
  const match = HEX_RE.exec(hex.trim());
  if (!match) {
    throw new Error(`Invalid hex color: "${hex}". Expected #RRGGBB.`);
  }
  const int = parseInt(match[1], 16);
  const channels = [(int >> 16) & 0xff, (int >> 8) & 0xff, int & 0xff].map((c) => {
    const norm = c / 255;
    return norm <= 0.03928 ? norm / 12.92 : Math.pow((norm + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

// ── Internals ─────────────────────────────────────────────────────────────

interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

function hexToHsl(hex: string): Hsl {
  const match = HEX_RE.exec(hex.trim());
  if (!match) {
    throw new Error(`Invalid hex color: "${hex}". Expected #RRGGBB.`);
  }
  const int = parseInt(match[1], 16);
  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / delta + 2) * 60;
        break;
      default:
        h = ((r - g) / delta + 4) * 60;
        break;
    }
  }

  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = clamp(s, 0, 100) / 100;
  const lNorm = clamp(l, 0, 100) / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const hSegment = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hSegment % 2) - 1));

  let r = 0;
  let g = 0;
  let b = 0;
  if (hSegment < 1) {
    r = c;
    g = x;
  } else if (hSegment < 2) {
    r = x;
    g = c;
  } else if (hSegment < 3) {
    g = c;
    b = x;
  } else if (hSegment < 4) {
    g = x;
    b = c;
  } else if (hSegment < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const m = lNorm - c / 2;
  return `#${toHex(r + m)}${toHex(g + m)}${toHex(b + m)}`;
}

function toHex(channel: number): string {
  return Math.round(clamp(channel, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0');
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}
